import { useMemo, useRef, useState } from 'react'

import type { PlaceResult } from 'src/features/business-create/components/bc-desired-location-selector'
import BusinessDesiredLocationSelector from 'src/features/business-create/components/bc-desired-location-selector'
import BusinessMap from 'src/features/business-create/components/business-map'
import LocationPill from 'src/features/business-create/components/ui/bc-location-pill'
import { useBusinessCreateContext } from 'src/features/business-create/context/business-create-context'
import { useBusinessCreateStore } from 'src/features/business-create/store/business-create-store'
import type { DesiredLocation } from 'src/features/consumers/schemas/consumer'

const BusinessCreateGeneralLocationsStep = () => {
  const mapRef = useRef<google.maps.Map>(null)
  const { setFormData, formData } = useBusinessCreateStore()
  const { handleNext } = useBusinessCreateContext()
  const [selectedLocations, setSelectedLocations] = useState<(DesiredLocation & { placeId: string })[]>([])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    e.stopPropagation()

    setFormData({
      ...formData,
      desiredLocations: selectedLocations.map((location) => ({
        latitude: location.latitude,
        longitude: location.longitude,
        name: location.name,
      })),
    })
    handleNext()
  }

  const onPlaceSelected = (place: PlaceResult) => {
    setSelectedLocations([
      ...selectedLocations,
      {
        latitude: place.latitude,
        longitude: place.longitude,
        name: place.city + ', ' + place.state,
        placeId: place.placeId,
      },
    ])

    if (mapRef.current) {
      mapRef.current.setCenter({
        lat: place.latitude,
        lng: place.longitude,
      })
    }
  }

  const markers = useMemo(() => {
    return selectedLocations.map((location) => ({
      lat: location.latitude,
      lng: location.longitude,
      name: location.name,
    }))
  }, [selectedLocations])

  return (
    <div className="relative flex size-full">
      <BusinessMap ref={mapRef} borderRadius="0px" zoom={6} draggable={true} zoomControl={false} markers={markers} />
      <div className="absolute inset-x-0 bottom-0 z-[100] w-full items-center px-5 py-8">
        <div className="flex flex-row items-center justify-between rounded-[40px] bg-white px-10 py-8">
          <div className="flex flex-col">
            <h1 className="text-[32px] font-semibold">Desired locations</h1>
            <p className="text-lg font-normal leading-normal text-muted">Enter city, state or postal code</p>
            <div className="mt-5 flex flex-row flex-wrap gap-2">
              {selectedLocations.map((location) => (
                <LocationPill
                  key={location.name}
                  location={location.name}
                  onRemoveLocation={() =>
                    setSelectedLocations(selectedLocations.filter((l) => l.name !== location.name))
                  }
                />
              ))}
            </div>
          </div>
          <form id="business-create-form" className="self-end" onSubmit={(e) => onSubmit(e)}>
            <BusinessDesiredLocationSelector onPlaceSelected={onPlaceSelected} selectedLocations={selectedLocations} />
          </form>
        </div>
      </div>
    </div>
  )
}

export default BusinessCreateGeneralLocationsStep
